/**
 * The sheets-ui test-harness seams: their door keys and shapes.
 *
 * The typed door is the workspace-only `@tento-lona/sheets-ui/internal-testing`
 * (`calendarDaysStripInstrumentationOf`, `sheetLayoutInstrumentationOf`). A browser
 * harness that cannot import that entry point (a ux-test evaluating inside the
 * app bundle) reaches the same object through the element's `Symbol.for` door:
 *
 *   row[Symbol.for("@tento-lona/sheets-ui/testing:calendarDaysRowInstrumentation")]()
 *   sheet[Symbol.for("@tento-lona/sheets-ui/testing:sheetLayoutInstrumentation")]()
 *   sheet[Symbol.for("@tento-lona/sheets-ui/testing:sheetRevealInstrumentation")]()
 *
 * — the same pattern as the SDK's `instrumentationOf`
 * (`@tento-lona/internal-testing`). The doors are non-enumerable and on no typed
 * surface, and opt-in: only `installUiHarnessDoors()` (the workspace-only
 * `@tento-lona/sheets-ui/internal-testing`, called once by an app running
 * under a harness) installs them —
 * loading the elements installs none. Everything a
 * seam hands out is LIVE: a harness that patches it changes how the element
 * behaves. This module is a leaf (type-only edges to the elements). The key
 * strings keep their `…/testing:` prefix: they are wire names ux specs spell
 * literally, not import paths.
 */

import type { Option, RowLocalId, Unsubscribe } from "@tento-lona/sheets";
import type { BindGeneration, BindQueueState } from "@tento-scroll/engine/scroll-engine";
import type { NumRangeLike } from "@tento-core/range";
import type { CalendarDay } from "@tento-ui/components/calendar/calendar-day";
import type { ScrollHorizontal } from "@tento-ui/components/scroll/scroll-horizontal";
import type { SheetLayoutPort } from "@tento-ui/components/sheet/sheet-layout";
import type { SheetBinder } from "./sheet/sheet-index-renderer.ts";
import type { CalendarDaysStrip } from "./plugins/layout/timeline-column/components/calendar-days-strip.ts";
import type { PendingGhostRenderer } from "./sheet/gestures/drag-ghost-service.ts";
import type { StripObserver } from "./plugins/layout/timeline-column/components/calendar-days-strip-observers.ts";

/** The in-page door key onto a `lona-calendar-days-row` element's seam. */
export const CALENDAR_DAYS_ROW_INSTRUMENTATION_KEY: unique symbol = Symbol.for(
  "@tento-lona/sheets-ui/testing:calendarDaysRowInstrumentation",
);

/** The in-page door key onto a `std-sheet` element's seam. */
export const SHEET_LAYOUT_INSTRUMENTATION_KEY: unique symbol = Symbol.for(
  "@tento-lona/sheets-ui/testing:sheetLayoutInstrumentation",
);

/** The in-page door key onto a `std-sheet` element's reveal-contract seam. */
export const SHEET_REVEAL_INSTRUMENTATION_KEY: unique symbol = Symbol.for(
  "@tento-lona/sheets-ui/testing:sheetRevealInstrumentation",
);

/**
 * A calendar days row, white-box: its bound read inputs and registrations,
 * observers of its private bind and paint steps, and the span-paint state a
 * paint fixture drives.
 */
export interface CalendarDaysStripInstrumentation {
  /**
   * The props of the row's last bind — its read revision (`readRevision`),
   * subcolumns, date window — or `null` before the first bind. Live: the
   * object the row reads, not a copy.
   */
  boundProps(): Option<CalendarDaysStrip.Props>;
  /**
   * The day-background theme the row's read inputs include, or `null` before
   * `init`. Live: patching its `backgroundColor` changes what the next read
   * resolves.
   */
  themeBackground(): Option<CalendarDay.Theme>;
  /** Whether the row holds its pending-event renderer registration. */
  hasPendingRenderer(): boolean;
  /**
   * Drop the pending-event renderer registration (as a recycle does), so the
   * next bind must register it again and cannot reuse a completed read.
   */
  dropPendingRenderer(): void;
  /**
   * The row's bind generation: every `bind`, `toggleLoading`, recycle and
   * disconnect bumps it, and an async paint of an older generation is
   * dropped. `0` for a row no constructor ran on.
   */
  bindGeneration(): number;
  /**
   * Observe the row's synchronous bind — the private step a completed
   * same-revision read skips, so counting these events counts the reads
   * that ran. The observer runs before the step with the props and the
   * generation; a function it returns runs after the step (returned or
   * thrown). Returns the unsubscribe. A row with no observer pays one null
   * check per bind.
   */
  onBind(observer: StripObserver<CalendarDaysStripInstrumentation.BindEvent>): Unsubscribe;
  /**
   * Observe every span paint on the row (a provider's resolved spans, a
   * drag-ghost repaint, a draft repaint, a `paintSpans` call), the empty
   * no-op included, before it runs. Returns the unsubscribe. A row with no
   * observer pays one null check per paint.
   */
  onPaint(observer: StripObserver<CalendarDaysStripInstrumentation.PaintEvent>): Unsubscribe;
  /** Observe a provider render request before it runs; separate from its eventual paint. */
  onRenderRequest(observer: StripObserver<CalendarDaysStripInstrumentation.RenderRequestEvent>): Unsubscribe;
  /**
   * The last render context per span layer (`$layout`), which a ghost or
   * draft change repaints from. Live and read-only: the map the row paints
   * from (written by the row's `SpanContexts` only); empty for a row no
   * constructor ran on.
   */
  spanContexts(): ReadonlyMap<HTMLElement, CalendarDaysStrip.SpanRenderCtx>;
  /** Swap in a copy of `next` as the row's span contexts; returns the map it replaced. */
  replaceSpanContexts(
    next: Map<HTMLElement, CalendarDaysStrip.SpanRenderCtx>,
  ): Map<HTMLElement, CalendarDaysStrip.SpanRenderCtx>;
  /** The in-flight existing-item drag ghost the row paints, or `null`. */
  dragGhost(): Option<CalendarDaysStrip.DragGhost>;
  /** Set (or clear, with `null`) the drag ghost without repainting. */
  setDragGhost(ghost: Option<CalendarDaysStrip.DragGhost>): void;
  /** Paint `ctx` into the span layer `$layout` now, as a resolved provider read does. */
  paintSpans($layout: HTMLElement, ctx: CalendarDaysStrip.SpanRenderCtx): void;
  /**
   * Deliver an existing-item drag update as the pending-event broadcast
   * does: a ghost repaints every live layer with it (`settle` unused);
   * `null` settles the drag — `"revert"` repaints the original position,
   * `"commit"` keeps the last ghost's.
   */
  applyExistingGhost(
    ghost: Option<CalendarDaysStrip.DragGhost>,
    settle: PendingGhostRenderer.Settle,
  ): void;
  /** Forget the completed read, so the next bind reads even at the same revision. */
  clearCompletedRead(): void;
}

export namespace CalendarDaysStripInstrumentation {
  /** One request to read/render spans, including requests whose paint is deferred or reused. */
  export interface RenderRequestEvent {
    readonly row: CalendarDaysStrip;
    readonly generation: number;
  }

  /** One synchronous bind of a calendar days row (see `onBind`). */
  export interface BindEvent {
    /** The row being bound. */
    readonly row: CalendarDaysStrip;
    /** The props it binds (`date[0]` is the week), not yet its `boundProps()`. */
    readonly props: CalendarDaysStrip.Props;
    /** The bind generation the bind runs in. */
    readonly generation: number;
  }

  /** One span paint of a calendar days row (see `onPaint`). */
  export interface PaintEvent {
    /** The row painting. */
    readonly row: CalendarDaysStrip;
    /** The span layer painted into, before the paint changes it. */
    readonly layout: HTMLElement;
    /** The render context painted: spans, row id, column, windowed fragment. */
    readonly ctx: CalendarDaysStrip.SpanRenderCtx;
    /** The row's bind generation at the paint. */
    readonly generation: number;
    /** Whether a drag ghost is in flight, so the paint projects it. */
    readonly hasDragGhost: boolean;
  }
}

/** A `std-sheet` element, white-box: the layout it drives and its scroller. */
export interface SheetLayoutInstrumentation {
  /**
   * The horizontal scroll host (`#scroll` in the layout's shadow root): its
   * `scrollEngine` (bind queue, window, viewport) and light-DOM columns —
   * what the scroll benches drive and measure.
   */
  readonly scroll: ScrollHorizontal<HTMLElement, HTMLElement, HTMLElement>;
  /** The layout surface the sheet drives (columns, children, overlays). */
  readonly layout: SheetLayoutPort;
  /** The binder a column whose renderer names none binds through. */
  readonly defaultBinder: SheetBinder;
}

/**
 * A `std-sheet` element, white-box onto its reveal-contract predicate: the
 * private members `presentationReady()` gates on (`scrubbedAtBindGeneration`,
 * the bound view-model's row layout) that a harness which never calls
 * `bind()` has no other way to reach. Live: `seedLayout` replaces the
 * element's `sheet` field outright.
 */
export interface SheetRevealInstrumentation {
  /** The private reveal-contract predicate the presentation ticket gates on. */
  presentationReady(): boolean;
  /**
   * Set (or clear, with `null`) the bind generation a scrub incurred
   * settle-debt at — the private `scrubbedAtBindGeneration` field.
   */
  setScrubbedAtBindGeneration(generation: Option<BindGeneration>): void;
  /**
   * Install a bound row layout (each row's height, by its raw id) as the
   * element's view-model layout, and reset the private reveal bookkeeping
   * (`revealGeneration`, `presentationStrip`, `presentationVia`,
   * `scrubbedAtBindGeneration`) a fresh bind starts from — the seam a
   * harness that never calls `bind()` uses to reach `presentationReady()`'s
   * real path.
   */
  seedLayout(heightsByRawId: ReadonlyMap<string, number>): void;
}

export namespace SheetRevealInstrumentation {
  /**
   * What the reveal-contract predicate reads off a `std-sheet` element —
   * a `SheetElement` hands its own state over; a harness builds one of its
   * own and runs the predicate against it (`sheetRevealReadyOf`). Live: the
   * predicate records the strip it judged (`recordStrip`).
   */
  export interface Host {
    /** The bound view model's row layout, or `null` for an empty shell (ready). */
    readonly sheet: Option<{
      readonly layout: {
        readonly rowLayout: ReadonlyMap<RowLocalId, { readonly cellHeightPx: number }>;
      };
    }>;
    /** The column strip: its bind state, strips and rendered columns. */
    readonly columns: Pick<
      SheetLayoutPort["columns"],
      "geometryBound" | "visibleIndices" | "renderedIndices" | "renderedElements"
    >;
    /** The scroll port's bind queue. */
    bindQueue(): BindQueueState;
    /** The bind generation a scrub incurred settle-debt at; `null` when none. */
    readonly scrubbedAtBindGeneration: Option<BindGeneration>;
    /** Whether the host is in the document. */
    readonly isConnected: boolean;
    /** The host's rendered width. */
    readonly offsetWidth: number;
    /** The host's rendered height. */
    readonly offsetHeight: number;
    /** The host's box, for the hidden-host breadcrumb. */
    getBoundingClientRect(): { readonly left: number; readonly top: number; readonly width: number; readonly height: number };
    /** The reveal generation the breadcrumbs carry. */
    readonly revealGeneration: number;
    /** The strip the last reveal judged. */
    readonly presentationStrip: Option<NumRangeLike>;
    /** Record the strip the predicate judged: the next reveal's restrip check reads it. */
    recordStrip(strip: NumRangeLike): void;
    /** How the last settle drained, for the restrip breadcrumb. */
    readonly presentationVia: Option<string>;
  }
}
